import type {
  ActivityHour,
  ActivityLevel,
  ActivitySessions,
  MarketActivitySnapshot,
} from "@screener/protocol";
import { formatClockMinutes } from "@/density/format";
import { formatUsd } from "@/lib/labels";

/**
 * Подписи пометки активности рынка (§145). Ничего не считают: уровень,
 * множитель и сессии приходят от фронта готовыми и только называются словами.
 */

const LEVEL_WORD: Record<ActivityLevel, string> = {
  low: "низкая",
  normal: "обычная",
  high: "высокая",
};

const COLLECTING_TEXT = "Рынок: собираем статистику";
const NO_DATA_TEXT = "Рынок: нет данных";

/** Сессии через «+» на перекрытии; вне сессий и выходные — словами (§145 п. 6). */
export function activitySessionsText(sessions: ActivitySessions): string {
  switch (sessions.kind) {
    case "open":
      return sessions.labels.join(" + ");
    case "out_of_sessions":
      return "вне сессий";
    case "weekend":
      return "выходные";
  }
}

/** Множитель как прислан («×1.8»); без уровня его нет. */
export function activityMultiplierText(snapshot: MarketActivitySnapshot): string | null {
  return snapshot.state.kind === "level" ? `×${snapshot.state.ratio}` : null;
}

/** Полный текст пометки: «Рынок: высокая активность ×1.8 · США». */
export function activityMarkText(snapshot: MarketActivitySnapshot): string {
  const { state } = snapshot;
  switch (state.kind) {
    case "level":
      return `Рынок: ${LEVEL_WORD[state.level]} активность ×${state.ratio} · ${activitySessionsText(snapshot.sessions)}`;
    case "collecting":
      return COLLECTING_TEXT;
    case "no_data":
      return NO_DATA_TEXT;
  }
}

/** Цвет пометки токенами темы: цвета сторон стакана не используются (§145 п. 4). */
export function activityColorClass(snapshot: MarketActivitySnapshot): string {
  const { state } = snapshot;
  if (state.kind !== "level") {
    return "text-muted-foreground";
  }
  switch (state.level) {
    case "low":
      return "text-muted-foreground";
    case "normal":
      return "text-foreground";
    case "high":
      return "text-brand";
  }
}

/** Строка над графиком подсказки: множитель, уровень, сессия, время расчёта UTC. */
export function activityTooltipHeadline(snapshot: MarketActivitySnapshot): string {
  const { state } = snapshot;
  const head =
    state.kind === "level"
      ? `×${state.ratio} · ${LEVEL_WORD[state.level]}`
      : state.kind === "collecting"
        ? "Собираем статистику"
        : "Нет данных";
  return `${head} · ${activitySessionsText(snapshot.sessions)} · расчёт ${formatClockMinutes(snapshot.computed_at_ms)}`;
}

/** День, с которого появится сравнение: дата UTC `ГГГГ-ММ-ДД`. */
export function collectingFromText(comparisonFromMs: number): string {
  return `Сравнение появится с ${new Date(comparisonFromMs).toISOString().slice(0, 10)} (UTC)`;
}

/** Час UTC без суффикса: «14:00». */
export function activityClockHour(hourMs: number): string {
  return formatClockMinutes(hourMs).replace(" UTC", "");
}

/** Подсказка столбика графика: «14:00 UTC · 1.2B $ (обычно 900M $)». */
export function activityBarTitle(hour: ActivityHour): string {
  const volume = hour.volume_usd === undefined ? "нет данных" : `${formatUsd(hour.volume_usd)} $`;
  const base = `${activityClockHour(hour.hour_ms)} UTC · ${volume}`;
  return hour.baseline_usd === undefined
    ? base
    : `${base} (обычно ${formatUsd(hour.baseline_usd)} $)`;
}

/** Через сколько часов подписывать столбик под графиком (§145 п. 7). */
const TICK_EVERY_HOURS = 6;

/**
 * Подписи часов под графиком: подпись i-го часа стоит в колонке i (с 1) сетки
 * на `hours.length` колонок, то есть под своим столбиком.
 */
export function activityTicks(hours: readonly ActivityHour[]): { column: number; label: string }[] {
  return hours.flatMap((hour, index) =>
    index % TICK_EVERY_HOURS === 0
      ? [{ column: index + 1, label: activityClockHour(hour.hour_ms) }]
      : [],
  );
}

/**
 * Точка графика часа. Час без объёма — «нет данных»: столбик нулевой и не
 * рисуется, линия обычного объёма остаётся, если она есть. `Number` здесь
 * только для рисования, не для вычислений.
 */
export function activityChartPoint(hour: ActivityHour): {
  key: string;
  bar: number;
  line: number | null;
} {
  return {
    key: String(hour.hour_ms),
    bar: hour.volume_usd === undefined ? 0 : Number(hour.volume_usd),
    line: hour.baseline_usd === undefined ? null : Number(hour.baseline_usd),
  };
}
